import { FC, useEffect, useState } from "react";
import { Grid, Breadcrumbs } from '@mui/material';
import CustomTable from "../../components/common/CustomTable/CustomTable";
import { useSupplierStore } from "../../hooks/useSupplierStore";
import { emphasize, styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { AccountReceivableDrawer } from "./components/drawers/AccountReceivableDrawer";
import { AccountPaidDrawer } from "./components/drawers/AccountPaidDrawer";
import { PayDrawer } from "./components/drawers/PayDrawer";
import { moneyCL } from "../../toolbox/helpers/money-helpers";
import { PaymentVoucherFormDrawer } from "../Payment/components/drawers/PaymentVoucherFormDrawer";


const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[800];
    return {
        backgroundColor,
        height: theme.spacing(3),
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
    };
})

const columns = [
    { type: 'text', field: 'rut', label: 'Rut', align: 'center' },
    { type: 'text', field: 'name_rz', label: 'Nombre', align: 'center' },
    { type: 'text', field: 'type', label: 'Tipo', align: 'center', format: (row) => row.type === 1 ? 'Cliente' : (row.type == 2 ? 'Proveedor' : 'Cliente y Proveedor') },
    { type: 'text', field: 'accounts_receivable', label: 'N° Cuentas', align: 'center' },
    { type: 'text', field: 'amount_receivable', label: 'Total por cobrar', align: 'center', format: (row) => moneyCL(row.amount_receivable) },
    {
        type: 'text', field: 'accounts_receivable_expired', label: 'N° Cuentas vencidas', align: 'center',
        format: (row) => {
            return (
                <span style={{ color: row.accounts_receivable_expired > 0 ? 'red' : 'default' }}>
                    {row.accounts_receivable_expired}</span>
            )
        }
    },
    {
        type: 'text', field: 'amount_receivable_expired', label: 'Total vencido por cobrar', align: 'center',
        format: (row) => {
            return (
                <span style={{ color: row.accounts_receivable_expired > 0 ? 'red' : 'default' }}>
                    {moneyCL(row.amount_receivable_expired)}</span>
            )
        }
    },
    { type: 'detail', field: 'detail', label: '', align: 'center' },
];

export const AccountReceivable: FC = () => {
    const { getSuppliers } = useSupplierStore();

    const [loading, setLoading] = useState(false);
    const [suppliers, setSuppliers] = useState<any>([]);
    const [rowSelected, setRowSelected] = useState<any>('');
    const [showDrawer, setShowDrawer] = useState<string>('');

    const [search, setSearch] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)
    const [totalRows, setTotalRows] = useState<number>(0)

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'F2') {
                event.preventDefault()
                onAdd()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    useEffect(() => {
        if (search === '') {
            getSupplierApi()
        } else {
            const timer = setTimeout(() => getSupplierApi(), 500)
            return () => clearTimeout(timer)
        }
    }, [page, perPage, search])

    const getSupplierApi = async () => {
        try {
            setLoading(true);

            const payload: any = {
                page: page,
                perPage: perPage,
                accountReceivable: true
            }
            if (search !== '') payload.search = search

            const response = await getSuppliers(payload);
            if (!response) {
                setSuppliers([]);
                setTotalRows(0)
                setLoading(false);
                return;
            } else {
                setSuppliers(response.detail);
                setTotalRows(response.total)
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
        }
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const onDetails = (rowSelected) => {
        setRowSelected(rowSelected);
        setShowDrawer('AccountPaid')
    }

    const onSearch = (search) => {
        setSearch(search)
    }

    const onAdd = () => {
        setShowDrawer('PaymentVoucherFormDrawer')
    }

    const onPay = (rowSelected) => {
        setRowSelected(rowSelected);
        setShowDrawer('PayDrawer')
    }

    const accountsPayable = (rowSelected) => {
        setRowSelected(rowSelected)
        setShowDrawer('AccountReceivable')
    }

    return (
        <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 3 }}>
                <StyledBreadcrumb
                    label={"Contabilidad"}
                />
                <StyledBreadcrumb
                    label={"Cuentas por cobrar"}
                    onClick={() => {
                        setRowSelected('');
                        getSupplierApi();
                    }}
                />
            </Breadcrumbs>

            <Grid item xs={12}>
                <CustomTable
                    title={'Cuentas por cobrar'}
                    columns={columns}
                    loading={loading}
                    rows={suppliers || []}
                    onRowClick={() => { }}
                    hasOptions
                    onDetails={onDetails}
                    onSearch={onSearch}
                    onAddFn={onAdd}
                    onAddFnName={'Registrar nuevo pago (F2)'}
                    dense

                    // onHelper={onPay}
                    // tooltip_helper="Abonar"
                    // icon_helper={<AttachMoneyIcon fontSize="small" />}

                    onHelper2={accountsPayable}
                    tooltip_helper2="Cuentas por cobrar"
                    icon_helper2={<RequestQuoteIcon fontSize="small" />}

                    totalRows={totalRows}
                    rowsPerPage={perPage}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    withCustomPagination
                />
            </Grid>
            {showDrawer === 'AccountReceivable' && (
                <AccountReceivableDrawer
                    open={showDrawer === 'AccountReceivable'}
                    onClose={() => setShowDrawer('')}
                    customer={rowSelected}
                    onList={getSupplierApi}
                />
            )}
            {showDrawer === 'AccountPaid' && (
                <AccountPaidDrawer
                    open={showDrawer === 'AccountPaid'}
                    onClose={() => setShowDrawer('')}
                    customer={rowSelected}
                />
            )}
            {showDrawer === 'PayDrawer' && (
                <PayDrawer
                    open={showDrawer === 'PayDrawer'}
                    onClose={() => setShowDrawer('')}
                    customer={rowSelected}
                />
            )}
            {showDrawer === 'PaymentVoucherFormDrawer' && (
                <PaymentVoucherFormDrawer
                    open={showDrawer === 'PaymentVoucherFormDrawer'}
                    onClose={() => setShowDrawer('')}
                    onList={getSupplierApi}
                />
            )}
        </>
    )
}